import { logger } from 'utils/system/logger'
import { AbstractPlayer } from './abstract-player'
import { ConnatixScriptService } from 'services/script/connatix-script.service'
import { attachEventHandlerWithFemFlagCheck } from 'utils/events'
import { LocalStorageKey } from 'constants/enums'
import { getAlphaNumericRandomStr } from 'utils/string'

export class ConnatixPlayer extends AbstractPlayer {
    private playerId: string
    private connatixPlayerLoaded = false
    private isNonInteraction = true
    private isPlaying = false
    public constructor(element: HTMLElement) {
        super(element)
        this.iframeData = JSON.parse(this.element.dataset.evtVal || '{}')
    }

    render() {
        attachEventHandlerWithFemFlagCheck('pnIdReady', async () => {
            if (this.connatixPlayerLoaded) {
                logger('warn', 'Connatix player already loaded')
                return
            }
            new ConnatixScriptService()
                .loadConnatixScript()
                .then((loaded) => {
                    if (loaded) {
                        this.init().catch((error: Error) =>
                            logger('warn', error)
                        )
                    }
                })
                .catch((error: Error) =>
                    logger('error', 'Connatix player failed to load', error)
                )
        })
    }

    async init(): Promise<void> {
        const wrapper = document.createElement('div')
        const dataStoreScript = document.createElement('script')

        this.playerId = this.getPlayerId()
        const videoId = this.element.dataset.videoId || ''
        const SCRIPT_ID_LENGTH = 32
        const scriptId = getAlphaNumericRandomStr(SCRIPT_ID_LENGTH)

        document.addEventListener('connatixCustomEvent', (ev: CustomEvent) => {
            this.handleCustomEvent(ev, videoId)
        })

        dataStoreScript.id = scriptId
        dataStoreScript.innerHTML = `
            (new Image()).src = 'https://tr.thecontentserver.com/tr/si?token=${this.playerId}&cid=47233f53-89b8-4723-b9eb-5cff41c16bc1';
            cnxel.cmd.push(function() {
                cnxel({
                    playerId: "${this.playerId}",
                    mediaId: "${videoId}",
                }).render("${scriptId}", (renderError, playerApi) => {

                    if (renderError) {
                        // An error occured while rendering the player
                        const errorMessage = renderError.type === cnx.configEnums.ErrorTypesEnum.NoPlayer ? "Player was not served or id given is incorrect" : renderError.message;
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "error", videoId: "${videoId}", errorMessage } }));
                        return;
                    }

                    // Pause all other players when a player is played
                    document.addEventListener('connatixExternalPlayerPlayed', () => {
                        playerApi.pause();
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "pauseClicked", videoId: "${videoId}" } }));
                    });

                    // Pause all connatix players when a player is played
                    document.addEventListener('connatixInternalPlayerPlayed', (ev) => {
                        if (ev.detail.videoId !== "${videoId}") {
                            playerApi.pause();
                            document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "pauseClicked", videoId: "${videoId}" } }));
                        }
                    });

                    playerApi.on("play", function() {
                        const videoData = playerApi.getVideoDetails();
                        const videoTitle = videoData.title;
                        const videoQuality = playerApi.getQuality();
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "play", videoId: "${videoId}", videoTitle: videoTitle, videoQuality: videoQuality } }));
                    });
                    playerApi.on("videoStarted", function(event) {
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "videoStarted", videoId: "${videoId}", objectId: event.id } }));
                    });
                    playerApi.on("videoCompleted100", function() {
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "videoCompleted100", videoId: "${videoId}" } }));
                    });
                    playerApi.on("adImpression", function() {
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "adImpression", videoId: "${videoId}" } }));
                        document.dispatchEvent(new CustomEvent('connatixInternalPlayerPlayed', { detail: { videoId: "${videoId}" } }));
                    });
                    playerApi.on("adPlay", function() {
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "adPlay", videoId: "${videoId}" } }));
                        document.dispatchEvent(new CustomEvent('connatixInternalPlayerPlayed', { detail: { videoId: "${videoId}" } }));
                    });
                    playerApi.on("adCompleted100", function() {
                        document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "adCompleted100", videoId: "${videoId}" } }));
                    });
                    playerApi.on('apiButtonClicked', (id) => {
                        const videoQuality = playerApi.getQuality();
                        if (id === "play-button") {
                            document.dispatchEvent(new CustomEvent('connatixInternalPlayerPlayed', { detail: { videoId: "${videoId}" } }));
                            document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "playClicked", videoId: "${videoId}", videoQuality: videoQuality } }));
                        } else if (id === "pause-button") {
                            document.dispatchEvent(new CustomEvent('connatixCustomEvent', { detail: { eventName: "pauseClicked", videoId: "${videoId}" } }));
                        }
                    });
                });
            });
        `
        wrapper.appendChild(dataStoreScript)
        this.element.appendChild(wrapper)
        this.connatixPlayerLoaded = true
        logger('info', 'Connatix player loaded')

        return Promise.resolve()
    }

    private getPlayerId(): string {
        if (this.userType === 'anonymous' || this.userType === 'register') {
            return this.element.dataset.nonSubPlayerId || ''
        } else {
            return this.element.dataset.subPlayerId || ''
        }
    }

    /**
     * Handles custom video events by dispatching them to the appropriate event handler.
     *
     * @param ev - The custom event object containing event details.
     * @param videoId - The ID of the video associated with the event.
     */
    private handleCustomEvent(ev: CustomEvent, videoId: string) {
        const eventHandlers: {
            [key: string]: (ev: CustomEvent, videoId: string) => void
        } = {
            play: this.handlePlayEvent.bind(this),
            playClicked: this.handlePlayClickedEvent.bind(this),
            videoStarted: this.handleVideoStartedEvent.bind(this),
            videoCompleted100: this.handleVideoCompletedEvent.bind(this),
            pauseClicked: this.handlePauseClickedEvent.bind(this),
            adImpression: this.handleAdImpressionEvent.bind(this),
            adPlay: this.handleAdPlayEvent.bind(this),
            adCompleted100: this.handleAdCompletedEvent.bind(this),
            error: this.handleErrorEvent.bind(this)
        }

        const handler = eventHandlers[ev.detail.eventName]
        if (handler) {
            handler(ev, videoId)
        }
    }

    /**
     * Handles the 'play' event for the video player.
     * Updates the video title and playback quality when the event is triggered.
     *
     * @param ev - The custom event containing video details.
     * @param videoId - The ID of the video being played.
     */
    private handlePlayEvent(ev: CustomEvent, videoId: string) {
        if (ev.detail.videoId === videoId) {
            this.videoTitle = ev.detail.videoTitle
            this.changeStickyTitle(ev.detail.videoTitle)
            this.playbackQuality = `${ev.detail.videoQuality}`
        }
    }

    /**
     * Handles the play clicked event for a specific video.
     *
     * @param ev - The custom event triggered when the play button is clicked.
     * @param videoId - The ID of the video that is being played.
     */
    private handlePlayClickedEvent(ev: CustomEvent, videoId: string) {
        if (ev.detail.videoId === videoId) {
            // If the video is being played, set isNonInteraction to false and dispatch the analytics play event
            this.playbackQuality = `${ev.detail.videoQuality}`
            this.isNonInteraction = false
            this.dispatchAnalyticsPlay(false)
            this.isPlaying = true
        }
        this.pauseAllOther()
    }

    /**
     * Handles the 'videoStarted' event for the video player.
     * Dispatches the analytics play event if the video is the current video and either
     * the interaction is non-interaction or the object is different.
     *
     * @param ev - The custom event containing video details.
     * @param videoId - The ID of the video being started.
     */
    private handleVideoStartedEvent(ev: CustomEvent, videoId: string) {
        const isCurrentVideo = ev.detail.videoId === videoId
        const isDifferentObject =
            ev.detail.objectId && ev.detail.objectId !== ev.detail.videoId
        if (isCurrentVideo && (this.isNonInteraction || isDifferentObject)) {
            this.dispatchAnalyticsPlay(true)
            this.isPlaying = true
        }
    }

    /**
     * Handles the video completed event.
     *
     * @param ev - The custom event triggered when the video is completed.
     * @param videoId - The ID of the video that has completed.
     *
     * This method checks if the video ID from the event matches the provided video ID.
     * If they match, it dispatches an analytics complete event and sets `isNonInteraction`
     * to true, indicating that the video has completed and user interaction is no longer required.
     */
    private handleVideoCompletedEvent(ev: CustomEvent, videoId: string) {
        if (ev.detail.videoId === videoId) {
            this.dispatchAnalyticsComplete()
            // Set isNonInteraction to true to indicate that the video has completed and user interaction is no longer required
            this.isNonInteraction = true
        }
    }

    /**
     * Handles the 'pauseClicked' event for the video player.
     * Dispatches the analytics paused event when the pause button is clicked.
     *
     * @param ev - The custom event containing video details.
     * @param videoId - The ID of the video being paused.
     */
    private handlePauseClickedEvent(ev: CustomEvent, videoId: string) {
        if (ev.detail.videoId === videoId && this.isPlaying) {
            this.dispatchAnalyticsPaused()
            this.isPlaying = false
        }
    }

    /**
     * Handles the ad impression event for a specific video. Fires when an ad is loaded.
     *
     * This method is triggered when an ad impression event occurs. It checks if the event's video ID matches
     * the provided video ID. If they match, it calls the `onAdStart` method to handle the start of the ad.
     * Additionally, it pauses all other video players to ensure that only one ad is playing at a time.
     *
     * @param ev - The custom event object containing the ad impression details.
     * @param videoId - The ID of the video for which the ad impression event is being handled.
     */
    private handleAdImpressionEvent(ev: CustomEvent, videoId: string) {
        if (ev.detail.videoId === videoId) {
            this.onAdStart()
        }
        // Pause all other players to ensure only one ad is playing at a time
        this.pauseAllOther()
    }

    /**
     * Handles the 'adPlay' event for the video player. Fires when an ad starts playing.
     *
     * Pauses all other players to ensure only one ad is playing at a time.
     */
    private handleAdPlayEvent() {
        this.pauseAllOther()
    }

    /**
     * Handles the ad completed event.
     *
     * @param ev - The custom event triggered when an ad is completed.
     * @param videoId - The ID of the video for which the ad was completed.
     */
    private handleAdCompletedEvent(ev: CustomEvent, videoId: string) {
        if (ev.detail.videoId === videoId) {
            this.onAdEnd()
        }
    }

    /**
     * Handles the 'error' event for the video player.
     * Dispatches the analytics error event when an error occurs.
     *
     * @param ev - The custom event containing error details.
     * @param videoId - The ID of the video where the error occurred.
     */
    private handleErrorEvent(ev: CustomEvent, videoId: string) {
        if (ev.detail.videoId === videoId) {
            this.dispatchAnalyticsError(ev.detail.errorMessage)
        }
    }

    private get userType(): 'anonymous' | 'subscriber' | 'register' {
        const isLoggedIn =
            localStorage.getItem(LocalStorageKey.LRTOKENKEY) || ''
        const userData = JSON.parse(
            localStorage.getItem(LocalStorageKey.XID) || '{}'
        ).data

        if (!isLoggedIn) {
            return 'anonymous'
        } else if (isLoggedIn && userData && userData.adLight) {
            return 'subscriber'
        }
        return 'register'
    }

    /**
     * Not implemented methods
     */
    get title() {
        return ''
    }
    get index() {
        return 0
    }
    setMute() {
        return
    }
    setControls() {
        return
    }
    setLoop() {
        return
    }
    setAutoplay() {
        return
    }
    pause() {
        return
    }
    play() {
        return
    }
    playVideoAt() {
        return
    }
    destroy() {
        return
    }
    cuePlaylist() {
        return
    }
    getState() {
        return ''
    }
    isPausable() {
        return true
    }
    onError() {
        return
    }
}
